<script setup lang="ts">
import { ref, computed, provide, watch, reactive } from 'vue'
import type { SidebarMenuItem, SidebarState } from '@/types/sidebar.types'
import { SIDEBAR_INJECTION_KEY } from '@/types/sidebar.types'
import JIcon from '@/components/atoms/JIcon.vue'
import JInput from '@/components/atoms/JInput.vue'
import JSidebarGroup from './JSidebarGroup.vue'
import JSidebarItem from './JSidebarItem.vue'
import { cn } from '@/lib/utils'

/**
 * JSidebar - 통합 사이드바 컴포넌트
 *
 * DB 트리 데이터 기반, provide/inject로 상태 공유.
 * collapsed 모드, 검색, 즐겨찾기(localStorage) 지원.
 * 라우터를 모름 — @menu-click으로 아이템을 올려보냄.
 */

const props = withDefaults(
  defineProps<{
    /** 메뉴 트리 데이터 */
    items: SidebarMenuItem[]
    /** 접힘 상태 (v-model:collapsed) */
    collapsed?: boolean
    /** 현재 활성 경로 */
    activePath?: string
    /** 펼침 너비 */
    width?: string
    /** 접힘 너비 */
    collapsedWidth?: string
    /** 즐겨찾기 localStorage 키 (없으면 즐겨찾기 비활성) */
    storageKey?: string
    /** 검색 표시 여부 */
    showSearch?: boolean
    /** 즐겨찾기 섹션 표시 여부 */
    showFavorites?: boolean
  }>(),
  {
    collapsed: false,
    width: '220px',
    collapsedWidth: '56px',
    showSearch: true,
    showFavorites: true,
  },
)

const emit = defineEmits<{
  'update:collapsed': [value: boolean]
  'menu-click': [item: SidebarMenuItem, event: MouseEvent]
}>()

// ── 즐겨찾기 (localStorage) ──
const favorites = ref<Set<string>>(new Set(loadFavorites()))

function loadFavorites(): string[] {
  if (!props.storageKey) return []
  try {
    const raw = localStorage.getItem(props.storageKey)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function saveFavorites() {
  if (!props.storageKey) return
  localStorage.setItem(props.storageKey, JSON.stringify([...favorites.value]))
}

function toggleFavorite(id: string) {
  if (favorites.value.has(id)) {
    favorites.value.delete(id)
  } else {
    favorites.value.add(id)
  }
  favorites.value = new Set(favorites.value) // trigger reactivity
  saveFavorites()
}

// ── provide/inject 상태 ──
const sidebarState = reactive<SidebarState>({
  collapsed: props.collapsed,
  activePath: props.activePath,
  favorites: favorites.value,
  toggleFavorite,
})

// props 변경 시 state 동기화
watch(() => props.collapsed, (v) => { sidebarState.collapsed = v })
watch(() => props.activePath, (v) => { sidebarState.activePath = v })
watch(favorites, (v) => { sidebarState.favorites = v })

provide(SIDEBAR_INJECTION_KEY, sidebarState)

// ── 검색 ──
const searchQuery = ref('')

/** 즐겨찾기 아이템 (L 타입만, 트리 평탄화) */
const favoriteItems = computed(() => {
  if (!props.storageKey || favorites.value.size === 0) return []
  const result: SidebarMenuItem[] = []
  const flatten = (items: SidebarMenuItem[]) => {
    for (const item of items) {
      if (item.menuType === 'L' && favorites.value.has(item.id)) {
        result.push(item)
      }
      if (item.children) flatten(item.children)
    }
  }
  flatten(props.items)
  return result
})

/** 검색 필터링 (재귀) */
const filteredItems = computed(() => {
  const q = searchQuery.value.trim().toLowerCase()
  if (!q) return props.items

  const filter = (items: SidebarMenuItem[]): SidebarMenuItem[] => {
    const result: SidebarMenuItem[] = []
    for (const item of items) {
      const matchLabel = item.label.toLowerCase().includes(q)
      const filteredChildren = item.children ? filter(item.children) : undefined
      if (matchLabel || (filteredChildren && filteredChildren.length > 0)) {
        result.push({ ...item, children: filteredChildren })
      }
    }
    return result
  }
  return filter(props.items)
})

/** 즐겨찾기 검색 필터링 */
const filteredFavorites = computed(() => {
  const q = searchQuery.value.trim().toLowerCase()
  if (!q) return favoriteItems.value
  return favoriteItems.value.filter(item => item.label.toLowerCase().includes(q))
})

// ── 즐겨찾기 그룹 펼침 ──
const favoritesExpanded = ref(true)

// ── 이벤트 핸들러 ──
const handleMenuClick = (item: SidebarMenuItem, event: MouseEvent) => {
  emit('menu-click', item, event)
}

const toggleCollapsed = () => {
  emit('update:collapsed', !props.collapsed)
}

// ── 사이드바 너비 ──
const sidebarWidth = computed(() => props.collapsed ? props.collapsedWidth : props.width)
</script>

<template>
  <aside
    class="h-full bg-background border-r border-border flex flex-col flex-shrink-0 overflow-hidden transition-[width] duration-200 ease-out"
    :style="{ width: sidebarWidth }"
  >
    <!-- 검색바 (펼침 + showSearch) -->
    <div v-if="!collapsed && showSearch" class="p-2 flex-shrink-0">
      <div class="relative">
        <JIcon
          name="search"
          size="sm"
          class="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <JInput
          v-model="searchQuery"
          placeholder="메뉴 검색..."
          class="pl-8 h-7 text-xs"
        />
      </div>
    </div>

    <!-- 메뉴 영역 -->
    <nav class="flex-1 overflow-y-auto overflow-x-hidden px-1 py-1">
      <!-- 즐겨찾기 섹션 -->
      <template v-if="showFavorites && storageKey && filteredFavorites.length > 0">
        <!-- 펼침: 즐겨찾기 그룹 -->
        <template v-if="!collapsed">
          <button
            class="flex items-center gap-1.5 w-full px-2 py-1.5 text-xs font-semibold text-yellow-600 cursor-pointer select-none transition-colors hover:text-yellow-700"
            @click="favoritesExpanded = !favoritesExpanded"
          >
            <JIcon
              name="chevronRight"
              size="sm"
              :class="cn(
                'flex-shrink-0 transition-transform duration-200',
                favoritesExpanded && 'rotate-90'
              )"
            />
            <JIcon name="star" size="sm" class="flex-shrink-0 text-yellow-500" />
            <span class="flex-1 text-left">즐겨찾기</span>
            <span class="text-[10px] text-yellow-500/60">{{ filteredFavorites.length }}</span>
          </button>
          <div
            :class="cn(
              'grid transition-[grid-template-rows] duration-200 ease-out',
              favoritesExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            )"
          >
            <div class="overflow-hidden">
              <JSidebarItem
                v-for="fav in filteredFavorites"
                :key="'fav-' + fav.id"
                :item="fav"
                @menu-click="handleMenuClick"
              />
            </div>
          </div>
        </template>
        <!-- 접힘: 별 아이콘 + 구분선 -->
        <template v-else>
          <div class="flex justify-center py-1">
            <JIcon name="star" size="sm" class="text-yellow-500" />
          </div>
          <JSidebarItem
            v-for="fav in filteredFavorites"
            :key="'fav-c-' + fav.id"
            :item="fav"
            @menu-click="handleMenuClick"
          />
        </template>
        <div class="h-px bg-border mx-2 my-1" />
      </template>

      <!-- 메인 메뉴 -->
      <template v-for="item in filteredItems" :key="item.id">
        <JSidebarGroup
          v-if="item.menuType === 'F'"
          :item="item"
          @menu-click="handleMenuClick"
        />
        <JSidebarItem
          v-else-if="item.menuType === 'L'"
          :item="item"
          @menu-click="handleMenuClick"
        />
      </template>

      <!-- 검색 결과 없음 -->
      <div
        v-if="filteredItems.length === 0 && searchQuery.trim()"
        class="text-center py-6 text-muted-foreground text-xs"
      >
        검색 결과가 없습니다.
      </div>
    </nav>

    <!-- 하단: collapse 토글 -->
    <div class="flex-shrink-0 border-t border-border p-1">
      <button
        class="flex items-center gap-2 w-full px-2 py-1 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors"
        @click="toggleCollapsed"
      >
        <JIcon
          :name="collapsed ? 'panelLeftOpen' : 'panelLeftClose'"
          size="sm"
        />
        <span v-if="!collapsed">접기</span>
      </button>
    </div>
  </aside>
</template>
